/**
 * StatusBar wrapper — 화면별 색상 / 스타일 전환.
 *
 * 사용 예 (Layout 컴포넌트 진입 시):
 *   useEffect(() => { setStatusBar({ style: 'dark', backgroundColor: '#FBD54E' }); }, []);
 */
import { useEffect } from "react";
import { isNative, nativeWarn } from "./index";

export type StatusBarStyle = "dark" | "light" | "default";

export interface StatusBarOptions {
  /** 'dark' = 검정 텍스트 (밝은 배경 위), 'light' = 흰 텍스트 (어두운 배경 위) */
  style?: StatusBarStyle;
  /** 6자리 hex (#RRGGBB) — Android 전용. iOS 는 무시 */
  backgroundColor?: string;
  /** WebView 가 statusbar 위로 올라갈지 (notch 처리) */
  overlay?: boolean;
}

export async function setStatusBar(options: StatusBarOptions): Promise<void> {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");

    if (options.style) {
      // ⚠ Capacitor Style 의미가 직관과 반대다:
      //   Style.Dark  = "어두운 배경용" → 흰색 텍스트/아이콘
      //   Style.Light = "밝은 배경용"   → 검정 텍스트/아이콘
      // 이 래퍼의 style 은 "원하는 아이콘 색" 기준(dark=검정, light=흰색)이므로 반대로 매핑.
      const map = {
        dark: Style.Light,
        light: Style.Dark,
        default: Style.Default,
      } as const;
      await StatusBar.setStyle({ style: map[options.style] });
    }
    if (options.backgroundColor) {
      await StatusBar.setBackgroundColor({ color: options.backgroundColor });
    }
    if (typeof options.overlay === "boolean") {
      await StatusBar.setOverlaysWebView({ overlay: options.overlay });
    }
  } catch (err) {
    nativeWarn("statusBar:set", err);
  }
}

export async function hideStatusBar(): Promise<void> {
  if (!isNative()) return;
  try {
    const { StatusBar } = await import("@capacitor/status-bar");
    await StatusBar.hide();
  } catch (err) {
    nativeWarn("statusBar:hide", err);
  }
}

export async function showStatusBar(): Promise<void> {
  if (!isNative()) return;
  try {
    const { StatusBar } = await import("@capacitor/status-bar");
    await StatusBar.show();
  } catch (err) {
    nativeWarn("statusBar:show", err);
  }
}

/** 페이지 mount 시 statusBar 자동 설정 hook */
export function useStatusBar(options: StatusBarOptions): void {
  useEffect(() => {
    void setStatusBar(options);
  }, [options.style, options.backgroundColor, options.overlay]);
}
